import React from "react";
import { Clock, BookOpen, ListChecks } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";
import { Course } from "@/shared/types";
import { formatDuration } from "@/lib/date-utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip";

interface CourseCardProps {
  course: Course;
  imageUrl?: string;
}

export const CourseCard = React.memo<CourseCardProps>(
  ({ course, imageUrl }) => {
    const navigate = useNavigate();

    const goToCourse = (courseId: number) => {
      navigate(`/courses/${courseId}`);
    };

    return (
      <Card
        onClick={() => goToCourse(course.id)}
        variant="elevated"
        className="group h-full flex flex-col overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer animate-fade-in"
      >
        <div className="relative h-48 overflow-hidden bg-[#363636] flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={course.title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <BookOpen className="h-16 w-16 text-[rgb(239,63,71)] transition-transform duration-300 group-hover:scale-110" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
        <div className="p-5 md:p-6 space-y-4 flex-1 flex flex-col justify-between min-h-[180px]">
          <div>
            {course.global && (
              <Badge
                variant="outline"
                className="mb-2 text-xs font-medium shadow-sm"
              >
                Global
              </Badge>
            )}

            <h3 className="font-semibold text-lg md:text-xl line-clamp-2 text-foreground group-hover:text-primary transition-colors duration-200 leading-tight">
              {course.title}
            </h3>
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5 transition-colors group-hover:text-foreground">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <span className="text-xs md:text-sm">
                  {formatDuration(course.estimatedDurationMinutes)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 transition-colors group-hover:text-foreground">
                <BookOpen className="h-4 w-4 flex-shrink-0" />
                <span className="text-xs md:text-sm">
                  {course.modules} módulos
                </span>
              </div>
              {Array.isArray(course.prerequisites) &&
                course.prerequisites.length > 0 && (
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1.5">
                        <ListChecks className="h-4 w-4 text-warning flex-shrink-0" />
                        <span className="text-warning font-semibold text-xs md:text-sm">
                          Prerrequisitos
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {course.prerequisites && course.prerequisites.length > 0 ? (
                          <div className="space-y-1">
                            {course.prerequisites.map((prereq) => (
                              <div key={prereq.courseId} className="text-sm">
                                <span className="font-medium">{prereq.title}</span>
                                {prereq.isCompleted && (
                                  <span className="ml-2 text-xs text-green-500">✓</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span>Cargando requisitos...</span>
                        )}
                      </TooltipContent>
                    </Tooltip>
                )}
            </div>
          </div>
          {course.progress !== undefined && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground font-medium">
                  Progreso
                </span>
                <span className="font-semibold text-foreground">
                  {course.progress}%
                </span>
              </div>
              <Progress value={course.progress} className="h-2.5 shadow-sm" />
            </div>
          )}
        </div>
      </Card>
    );
  }
);
